import { QuoteSaleType, QuoteStateCode, QuoteStatusCode, QuoteType, QuoteWarrantyTerm } from '@/lib/types'
import { requireServiceBillingContact } from '@/lib/billing-contact-rule'
import { agreementForQuote } from '@/lib/agreement-version'
import { cpqVersionForQuote } from '@/lib/cpq-version'
export { DISCOUNT_SPLIT_CUTOFF_DATE } from '@/lib/discount-line-policy'
import { getPreviousApprovedQuoteQuerySuffix } from '@/lib/quote-chain'
import { PREVIOUS_SNAPSHOT_QUERY_OPTIONS } from '@/lib/query-client'
import { getQuoteId } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { getMultipleRecords, getRecord } from './helpers'
import { MissingRequiredDataError } from './missing-required-data-error'
import {
  BRANCH_DEFAULT_TAX_CODE_FIELD,
  isKeepSuppliesUnchangedFieldMissingError,
  isQuoteFieldMissingError,
  KEEP_SUPPLIES_UNCHANGED_FIELD,
} from './quote-field-capability'
import { QUERY_KEYS } from './query-keys'

const systemUserSchema = z.object({
  systemuserid: z.string({ message: 'System user ID is missing.' }),
  fullname: z.string().nullable(),
  internalemailaddress: z.string().nullable(),
  mobilephone: z.string().nullable(),
})

let keepSuppliesUnchangedFieldSupported: boolean | undefined
let branchDefaultTaxCodeFieldSupported: boolean | undefined
const BRANCH_DEFAULT_TAX_CODE_EXPAND = `;$expand=${BRANCH_DEFAULT_TAX_CODE_FIELD}($select=msdyn_taxcodeid,msdyn_taxrate,msdyn_name,statecode)`

export type SystemUserData = z.infer<typeof systemUserSchema>

const salesOrderSchema = z.object({
  salesorderid: z.string({ message: 'Sales order ID is missing.' }),
  dsy_type: z.nativeEnum(QuoteType).nullable(),
  dsy_netsuitebomid: z.number().nullable().optional(),
})

export type SalesOrderData = z.infer<typeof salesOrderSchema>

export const quoteSchema = z
  .object({
    '@odata.etag': z.string().optional(),
    // Required fields
    quoteid: z.string({
      message: `ID is required.`,
    }),
    name: z.string({
      message: `Name is required.`,
    }),
    statecode: z.nativeEnum(QuoteStateCode, {
      message: `Status is required.`,
    }),
    revisionnumber: z.number({
      message: `Revision Number is required.`,
    }),
    _pricelevelid_value: z.string({
      message: `Price List is required. Please set the 'Price List' field on the Dynamics 'Quote' record.`,
    }),
    _customerid_value: z.string({
      message: `Account is required. Please set the 'Account' field on the Dynamics 'Quote' record.`,
    }),
    _dsy_contact_value: z.string({
      message: `Primary contact is required. Please set the 'Primary Contact' field on the Dynamics 'Quote' record.`,
    }),
    dsy_type: z.nativeEnum(QuoteType, {
      message: `Quote type is required. Please set the 'Quote Type' field on the Dynamics 'Quote' record.`,
    }),
    dsy_saletype: z.nativeEnum(QuoteSaleType, {
      message: `Sale type is required. Please set the 'Sale Type' field on the Dynamics 'Quote' record.`,
    }),
    // Empty lookups arrive as null from $expand — .nullable() + issue-raising
    // transform so the authored message fires (instead of zod's invalid_type)
    // while the output type stays non-null.
    dsy_Branch: z
      .object({
        dsy_branchid: z.string(),
        dsy_name: z.string().nullable(),
        dsy_legalentityname: z.string().nullable(),
        dsy_state: z.string().nullable(),
        dsy_phone: z.string().nullable(),
        dsy_paynowoncpqproposal: z.boolean().nullable(),
        dsy_accountingintegration: z.number().nullable().optional(),
        dsy_defaulttaxcode: z
          .object({
            msdyn_taxcodeid: z.string(),
            msdyn_taxrate: z.number(),
            msdyn_name: z.string().optional(),
            statecode: z.number().optional(),
          })
          .nullable()
          .optional(),
        dsy_logo: z.string().nullable().optional(),
      })
      .nullable()
      .transform((branch, ctx) => {
        if (!branch?.dsy_branchid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Branch is required. Please set the 'Branch' field on the Dynamics 'Quote' record.`,
          })
          return z.NEVER
        }
        return branch
      }),
    dsy_salesrep: systemUserSchema.nullable().transform((user, ctx) => {
      if (!user?.systemuserid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Sales Representative is required. Please set the 'Sales Representative' field on the Dynamics 'Quote' record.`,
        })
        return z.NEVER
      }
      return user
    }),

    // Nullable fields (required fields that are not set)
    dsy_suppliespricerate: z.number().nullable(),
    discountamount: z.number().nullable(),
    discountpercentage: z.number().nullable(),
    dsy_approvalsignaturedatetime: z.string().nullable(),
    dsy_approvalprintname: z.string().nullable(),
    proposal_layout_version: z.union([z.literal(1), z.literal(2)]).optional().catch(undefined),
    dsy_budgetlowvariance: z.number().nullable(),
    dsy_budgethighvariance: z.number().nullable(),
    dsy_projectoverview: z.string().nullable(),
    dsy_taxcode: z
      .object({
        msdyn_taxcodeid: z.string(),
        msdyn_taxrate: z.number(),
        msdyn_name: z.string().optional(),
        statecode: z.number().optional(),
      })
      .nullable(),
    dsy_salesorder: salesOrderSchema.nullable(),

    // Optional fields (opted out from proposal data)
    statuscode: z.nativeEnum(QuoteStatusCode).optional(),
    dsy_billingcontact: z
      .object({
        contactid: z.string(),
        fullname: z.string(),
        emailaddress1: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    dsy_suppliescostrate: z.number().nullable().optional(),
    dsy_keepsuppliesunchanged: z.boolean().nullable().optional(),
    dsy_defaultwirerunlength: z.number().nullable().optional(),
    dsy_defaultwirelooplength: z.number().nullable().optional(),
    _opportunityid_value: z.string().nullable().optional(),
    _dsy_workorder_value: z.string().nullable().optional(),
    dsy_netsuitechangeorderid: z.number().nullable().optional(),
    dsy_proposaldisplay_showroomprice: z.boolean().nullable().optional(),
    dsy_proposaldisplay_showsystemprice: z.boolean().nullable().optional(),
    dsy_proposaldisplay_showitemprice: z.boolean().nullable().optional(),
    dsy_proposaldisplay_defaultexpandedrooms: z.boolean().nullable().optional(),
    dsy_proposaldisplay_defaultexpandedsystems: z.boolean().nullable().optional(),
    dsy_proposaldisplay_defaultexpandedaccessories: z.boolean().nullable().optional(),
    dsy_proposaldisplay_showmodel: z.boolean().nullable().optional(),
    dsy_proposaldisplay_showroompriceasinstalled: z.boolean().nullable().optional(),
    dsy_proposaldisplay_showsystempriceasinstalled: z.boolean().nullable().optional(),
    dsy_proposaldisplay_groupbysystem: z.boolean().nullable().optional(),
    dsy_proposaldisplay_groupbyitemscope: z.boolean().nullable().optional(),
    dsy_proposaldisplay_summarymode: z.number().nullable().optional(),
    dsy_proposaldisplay_totalsmode: z.number().nullable().optional(),
    dsy_proposaldisplay_showpaymenthistory: z.boolean().nullable().optional(),
    // Packages (default false — packages collapse on the proposal unless opted in)
    dsy_proposaldisplay_defaultexpandedpackages: z.boolean().nullable().optional(),
    dsy_proposaldisplay_expandablepackages: z.boolean().nullable().optional(),
    // Packages (default true — consumers apply `?? true`)
    dsy_proposaldisplay_showpackageprice: z.boolean().nullable().optional(),
    dsy_warrantyterm: z.nativeEnum(QuoteWarrantyTerm).nullable().optional(),
    dsy_activatedon: z.string().nullable().optional(),
    dsy_dtoolssiprojectid: z.string().nullable().optional(),
    dsy_dtoolssilaststate: z.number().nullable().optional(),
    dsy_dtoolssilastpublishedon: z.string().nullable().optional(),
    dsy_dtoolssilastconfirmedon: z.string().nullable().optional(),
    dsy_dtoolssiconumber: z.number().nullable().optional(),
    quotenumber: z.string(),

    // Financial fields — read-only snapshot of server values, used by QuotePlugin
    // to gate redundant writes when summary already matches what's stored.
    msdyn_estimatedcost: z.number().nullable().optional(),
    totallineitemamount: z.number().nullable().optional(),
    totaldiscountamount: z.number().nullable().optional(),
    totaltax: z.number().nullable().optional(),
    totalamount: z.number().nullable().optional(),
    dsy_totalhours: z.number().nullable().optional(),
    opportunityid: z
      .object({
        opportunityid: z.string(),
        totallineitemamount: z.number().nullable().optional(),
        totaldiscountamount: z.number().nullable().optional(),
        estimatedvalue: z.number().nullable().optional(),
        totaltax: z.number().nullable().optional(),
        totalamount: z.number().nullable().optional(),
      })
      .nullable()
      .optional(),
  })
  .superRefine((quote, ctx) => {
    if (quote.dsy_type === QuoteType.ChangeOrder && !quote.dsy_salesorder?.salesorderid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dsy_salesorder'],
        message: `Sales order is required to create a change order.`,
      })
      return false
    }
    return true
  })

export type QuoteData = z.infer<typeof quoteSchema>

// Builder-only: the unauthenticated proposal page parses the same quoteSchema
// via proposal-api, whose payload does not expand dsy_billingcontact — scoping
// the rule here keeps customer-facing service proposals loading. The
// previous-quote snapshot (change-order diff base) also stays on the base
// schema: it is historical data nobody can or should repair.
const builderQuoteSchema = quoteSchema.superRefine(requireServiceBillingContact)

export async function getQuote(filters?: { previousQuote?: boolean }) {
  const currentQuoteId = getQuoteId()

  const branchTaxExpand = branchDefaultTaxCodeFieldSupported === false ? '' : BRANCH_DEFAULT_TAX_CODE_EXPAND
  const optionsWithSuppliesPolicy =
    `?$select=quoteid,quotenumber,name,revisionnumber,_pricelevelid_value,dsy_saletype,dsy_suppliespricerate,dsy_suppliescostrate,dsy_keepsuppliesunchanged,dsy_approvalsignaturedatetime,dsy_approvalprintname,discountamount,discountpercentage,statecode,statuscode,dsy_type,dsy_budgetlowvariance,dsy_budgethighvariance,dsy_defaultwirerunlength,dsy_defaultwirelooplength,dsy_projectoverview,dsy_netsuitechangeorderid,_customerid_value,_dsy_contact_value,_opportunityid_value,_dsy_workorder_value,dsy_proposaldisplay_showroomprice,dsy_proposaldisplay_showsystemprice,dsy_proposaldisplay_showitemprice,dsy_proposaldisplay_defaultexpandedrooms,dsy_proposaldisplay_defaultexpandedsystems,dsy_proposaldisplay_defaultexpandedaccessories,dsy_proposaldisplay_showmodel,dsy_proposaldisplay_showroompriceasinstalled,dsy_proposaldisplay_showsystempriceasinstalled,dsy_proposaldisplay_groupbysystem,dsy_proposaldisplay_groupbyitemscope,dsy_proposaldisplay_summarymode,dsy_proposaldisplay_totalsmode,dsy_proposaldisplay_showpaymenthistory,dsy_proposaldisplay_defaultexpandedpackages,dsy_proposaldisplay_showpackageprice,dsy_proposaldisplay_expandablepackages,dsy_warrantyterm,dsy_activatedon,dsy_dtoolssiprojectid,dsy_dtoolssilaststate,dsy_dtoolssilastpublishedon,dsy_dtoolssilastconfirmedon,dsy_dtoolssiconumber,msdyn_estimatedcost,totallineitemamount,totaldiscountamount,totaltax,totalamount,dsy_totalhours&$expand=dsy_salesorder($select=salesorderid,dsy_type,dsy_netsuitebomid),dsy_Branch($select=dsy_branchid,dsy_name,dsy_legalentityname,dsy_state,dsy_phone,dsy_paynowoncpqproposal,dsy_accountingintegration${branchTaxExpand}),dsy_taxcode($select=msdyn_taxcodeid,msdyn_taxrate,msdyn_name,statecode),dsy_salesrep($select=systemuserid,fullname,internalemailaddress,mobilephone),dsy_billingcontact($select=contactid,fullname,emailaddress1),opportunityid($select=opportunityid,totallineitemamount,totaldiscountamount,estimatedvalue,totaltax,totalamount)`
  const optionsWithoutSuppliesPolicy = optionsWithSuppliesPolicy.replace(
    `,${KEEP_SUPPLIES_UNCHANGED_FIELD}`,
    ''
  )
  let options =
    keepSuppliesUnchangedFieldSupported === false ? optionsWithoutSuppliesPolicy : optionsWithSuppliesPolicy

  let quote: unknown
  try {
    try {
      quote = await getRecord('quotes', currentQuoteId, options)
      if (options === optionsWithSuppliesPolicy) keepSuppliesUnchangedFieldSupported = true
    } catch (error) {
      if (!isKeepSuppliesUnchangedFieldMissingError(error)) throw error

      keepSuppliesUnchangedFieldSupported = false
      options = optionsWithoutSuppliesPolicy
      quote = await getRecord('quotes', currentQuoteId, options)
    }
  } catch (error) {
    if (branchDefaultTaxCodeFieldSupported === false || !isQuoteFieldMissingError(error, BRANCH_DEFAULT_TAX_CODE_FIELD)) {
      throw error
    }
    branchDefaultTaxCodeFieldSupported = false
    return getQuote(filters)
  }

  const parseResult = builderQuoteSchema.safeParse(quote)
  if (!parseResult.success) {
    throw new MissingRequiredDataError('quotes', [
      { id: currentQuoteId, raw: quote as Record<string, unknown>, issues: parseResult.error.issues },
    ])
  }
  const parsedQuote = parseResult.data

  document.title = parsedQuote.name

  if (filters?.previousQuote) {
    const salesOrder = parsedQuote.dsy_salesorder
    if (!salesOrder) {
      throw new Error('Sales order is required to create a change order.')
    }

    const previousQuotes = await getMultipleRecords<{ quoteid: string }>(
      'quotes',
      options + getPreviousApprovedQuoteQuerySuffix(salesOrder.salesorderid, parsedQuote.dsy_approvalsignaturedatetime)
    )

    const previousQuote = previousQuotes.at(0)
    if (!previousQuote) {
      throw new Error(
        'Sales Order "Quote" field is required to create a change order. Set the "Quote" field on the Sales Order.'
      )
    }
    return quoteSchema.parse(previousQuote)
  }

  return parsedQuote
}

export function useQuoteQuery(filters?: { previousQuote?: boolean }) {
  return useQuery({
    queryKey: QUERY_KEYS.quote(filters),
    queryFn: () => getQuote(filters),
    refetchOnWindowFocus: 'always',
    ...(filters?.previousQuote ? PREVIOUS_SNAPSHOT_QUERY_OPTIONS : {}),
  })
}

/**
 * @returns True if the quote is a service quote or a change order for a service invoice, false otherwise.
 */
export function useIsServiceQuoteOrChangeOrder() {
  const { data: quote } = useQuoteQuery()

  if (!quote) {
    return false
  }

  if (quote.dsy_type === QuoteType.ServiceInvoice) {
    return true
  }

  if (quote.dsy_type === QuoteType.ChangeOrder && quote.dsy_salesorder?.dsy_type === QuoteType.ServiceInvoice) {
    return true
  }

  return false
}

export { CPQ_VERSION_2_CUTOFF_DATE } from '@/lib/cpq-version'
const originalQuoteTaxCodeSchema = z
  .object({
    msdyn_taxcodeid: z.string(),
    msdyn_taxrate: z.number(),
    msdyn_name: z.string().optional(),
    statecode: z.number().optional(),
  })
  .nullable()

const originalQuoteSchema = z.object({
  quoteid: z.string(),
  dsy_activatedon: z.string().nullable().optional(),
  dsy_approvalsignaturedatetime: z.string().nullable(),
  dsy_taxcode: originalQuoteTaxCodeSchema,
})

export type OriginalQuoteData = z.infer<typeof originalQuoteSchema>

export async function getOriginalQuote(salesOrderId: string): Promise<OriginalQuoteData | null> {
  const records = await getMultipleRecords<OriginalQuoteData>(
    'quotes',
    `?$select=quoteid,dsy_activatedon,dsy_approvalsignaturedatetime` +
      `&$expand=dsy_taxcode($select=msdyn_taxcodeid,msdyn_taxrate,msdyn_name,statecode)` +
      `&$filter=_dsy_salesorder_value eq '${salesOrderId}' ` +
      `and statecode eq 2 ` +
      `and dsy_approvalsignaturedatetime ne null ` +
      `&$orderby=dsy_approvalsignaturedatetime asc` +
      `&$top=1`
  )
  const record = records.at(0)
  return record ? originalQuoteSchema.parse(record) : null
}

// Always uses current quote (no `previousQuote` filter). The legacy-chain decision is per-SO,
// shared across the whole chain — current vs previous summaries reach the same answer.
export function useOriginalQuoteQuery() {
  const { data: quote } = useQuoteQuery()
  const salesOrderId = quote?.dsy_salesorder?.salesorderid

  return useQuery({
    queryKey: QUERY_KEYS.originalQuote(salesOrderId),
    queryFn: () => (salesOrderId ? getOriginalQuote(salesOrderId) : null),
    enabled: !!salesOrderId && quote?.dsy_type === QuoteType.ChangeOrder,
    staleTime: Infinity,
  })
}

// Each side derives from its own quote ON PURPOSE: a legacy (pre-cutoff)
// chain computes its baseline under V1 so the first change order absorbs the
// V1→V2 computational differences and publishes them to NetSuite, healing the
// order onto V2 math. Once that CO is Won it becomes the (post-cutoff)
// predecessor and the chain computes V2 on both sides. Do not "fix" this to
// derive from the current quote — that strands the as-sold V1 discrepancy in
// NetSuite permanently.
export function useCPQVersion(previousQuote = false) {
  const { data: quote } = useQuoteQuery({ previousQuote })
  return cpqVersionForQuote(quote)
}

export const originalAgreementQuoteSchema = z.object({
  statecode: z.nativeEnum(QuoteStateCode),
  dsy_type: z.nativeEnum(QuoteType).nullable(),
  dsy_approvalsignaturedatetime: z.string(),
  dsy_warrantyterm: z.nativeEnum(QuoteWarrantyTerm).nullable().optional(),
})

// The project's original quote for agreement purposes: the earliest signed quote on the sales order that isn't
// a change order (unlike getOriginalQuote, which serves the money chain). See agreementForQuote.
export async function getOriginalAgreementQuote(salesOrderId: string) {
  const records = await getMultipleRecords(
    'quotes',
    `?$select=statecode,dsy_type,dsy_approvalsignaturedatetime,dsy_warrantyterm` +
      `&$filter=_dsy_salesorder_value eq '${salesOrderId}' ` +
      `and dsy_type ne ${QuoteType.ChangeOrder} ` +
      `and dsy_approvalsignaturedatetime ne null` +
      `&$orderby=dsy_approvalsignaturedatetime asc` +
      `&$top=1`
  )
  const record = records.at(0)
  return record ? originalAgreementQuoteSchema.parse(record) : null
}

// The agreement the current quote shows and is signed with. A change order waits until its original quote
// loads, so the agreement on screen doesn't swap after first paint and a failed lookup never falls back to the
// change order's own agreement (the API would refuse it).
export function useAgreement() {
  const { data: quote } = useQuoteQuery()
  const salesOrderId = quote?.dsy_salesorder?.salesorderid
  const isChangeOrder = quote?.dsy_type === QuoteType.ChangeOrder && !!salesOrderId
  const { data: originalQuote, isSuccess } = useQuery({
    queryKey: QUERY_KEYS.originalAgreementQuote(salesOrderId),
    queryFn: () => (salesOrderId ? getOriginalAgreementQuote(salesOrderId) : null),
    enabled: isChangeOrder,
    staleTime: Infinity,
  })
  return { ...agreementForQuote(quote, originalQuote), isPending: !quote || (isChangeOrder && !isSuccess) }
}
